import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../AuthContext'
import { api, fixImageUrl } from '../api'
import RinaContextCard from '../components/RinaContextCard'
import { useTheme } from '../theme'
import PageHub from '../components/PageHub'
import { avecAlpha } from '../lib/couleurs'

function ContenuOrganisation() {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const tm = 'text-ink/60'
  const cardBg = 'bg-card border-card-border'
  const inputBg = isWeb3 ? 'bg-white/5 text-white border-white/10' : 'bg-white text-ink border-ink/10'
  const labelBg = 'bg-ink/5'

  const [org, setOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showEdit, setShowEdit] = useState(false)
  const [toast, setToast] = useState(null)

  // Formulaire edition
  const [editForm, setEditForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState(null)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const loadData = useCallback(async () => {
    if (!token) return
    try {
      const data = await api.producer.getMe(token)
      setOrg(data)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadData() }, [loadData])

  // Ici on n'edite que le juridique/contact ; l'identite publique (enseigne,
  // photo, commune, presentation) s'edite dans « Mon profil producteur »
  // (un seul chemin d'edition par champ — v7.1 sujet 4).
  const openEdit = () => {
    if (!org) return
    setEditForm({
      telephone: org.telephone || org.owner?.telephone || '',
      email: (org.email && !org.email.endsWith('@phone.manrina.local') ? org.email : '') || (org.owner?.email && !org.owner.email.endsWith('@phone.manrina.local') ? org.owner.email : '') || '',
      adresseRue: org.adresseRue || '',
      codePostal: org.codePostal || '',
      siteWeb: org.siteWeb || '',
    })
    setEditError(null)
    setShowEdit(true)
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    setEditError(null)
    try {
      await api.producer.updateMe(editForm, token)
      setShowEdit(false)
      showToast('Organisation mise a jour !')
      loadData()
    } catch (err) {
      setEditError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center pt-10 animate-fade-up">
        <div className={`w-8 h-8 mx-auto border-2 border-t-transparent rounded-full animate-spin`} style={{ borderColor: avecAlpha('rgb(var(--c-primary))', 0.25), borderTopColor: 'transparent' }} />
        <p className={`text-xs mt-3 ${ts}`}>Chargement...</p>
      </div>
    )
  }

  if (!org) return null

  const editFields = [
    { key: 'telephone', label: 'Téléphone', placeholder: '0696 XX XX XX' },
    { key: 'email', label: 'Email de contact', placeholder: 'contact@monactivite.fr' },
    { key: 'adresseRue', label: 'Adresse', placeholder: 'Rue...' },
    { key: 'codePostal', label: 'Code postal', placeholder: '97200' },
    { key: 'siteWeb', label: 'Site web', placeholder: 'https://...' },
  ]

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[200] px-5 py-3 rounded-xl text-white text-sm font-medium shadow-xl animate-[fadeIn_0.3s_ease] ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* v7.2.4 : titre dans la bulle RINA — le retour vit dans le header */}
      <RinaContextCard
       
        title="Mon organisation"
        message="Vos coordonnées et informations légales. Elles ne sont jamais publiques — elles servent à la facturation et aux versements."
      />

      {/* Logo + nom */}
      <div className={`p-5 rounded-2xl border mb-4 animate-fade-up ${cardBg}`}>
        <div className="flex items-center gap-4 mb-4">
          <span
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white flex-shrink-0 overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-green)))' }}
          >
            {org.logo ? (
              <img src={fixImageUrl(org.logo)} alt="" className="w-full h-full object-cover" />
            ) : (
              (org.nom?.[0] || 'P').toUpperCase()
            )}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-lg font-bold truncate ${tc}`}>{org.nomCommercial || org.nom}</p>
            <p className={`text-xs ${ts}`}>{org.ville || 'Martinique'}</p>
            {org.status && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${
                org.status === 'VALIDE' ? (isWeb3 ? 'bg-green-500/20 text-green-300' : 'bg-green-50 text-green-700')
                : org.status === 'CANDIDAT' ? (isWeb3 ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700')
                : (isWeb3 ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-700')
              }`}>
                {org.status === 'VALIDE' ? 'Valide' : org.status === 'CANDIDAT' ? 'En attente' : org.status}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={openEdit}
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-green)))' }}
        >
          Modifier les coordonnées
        </button>
        <p className={`text-[10.5px] mt-2 text-center ${ts}`}>
          Enseigne, photo, commune et présentation se modifient dans <strong>Mon profil producteur</strong>.
        </p>
      </div>

      {/* Details organisation */}
      <div className={`p-5 rounded-2xl border animate-fade-up ${cardBg}`} style={{ animationDelay: '0.05s' }}>
        <h3 className={`font-semibold mb-3 ${tc}`}>Informations</h3>
        <div className="space-y-2.5">
          {[
            { label: 'Enseigne', value: org.nomCommercial || org.nom },
            org.description && { label: 'Description', value: org.description },
            { label: 'Type', value: org.type === 'PROFESSIONNEL' ? 'Professionnel' : org.type === 'ASSOCIATION' ? 'Association' : 'Particulier' },
            org.siret && { label: 'SIRET', value: org.siret },
            (org.email && !org.email.endsWith('@phone.manrina.local')) && { label: 'Email', value: org.email },
            org.telephone && { label: 'Téléphone', value: org.telephone },
            org.ville && { label: 'Commune', value: org.ville },
            org.adresseRue && { label: 'Adresse', value: org.adresseRue },
            org.codePostal && { label: 'Code postal', value: org.codePostal },
            org.siteWeb && { label: 'Site web', value: org.siteWeb },
          ].filter(Boolean).map((item, i) => (
            <div key={i} className={`p-3 rounded-xl ${labelBg}`}>
              <span className={`text-xs ${tm} block mb-0.5`}>{item.label}</span>
              <span className={`${tc} text-sm`}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modules actifs */}
      {org.moduleActivations?.length > 0 && (
        <div className={`p-5 rounded-2xl border mt-4 animate-fade-up ${cardBg}`} style={{ animationDelay: '0.1s' }}>
          <h3 className={`font-semibold mb-3 ${tc}`}>Modules actifs</h3>
          <div className="flex flex-wrap gap-2">
            {org.moduleActivations.map((ma) => (
              <span
                key={ma.id}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isWeb3 ? 'bg-green-500/20 text-green-300' : 'bg-green-50 text-green-700'}`}
              >
                {ma.module?.nom || ma.module?.code}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Modal edition */}
      {showEdit && editForm && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEdit(false)} />
          <div className={`relative w-full max-w-lg max-h-[90dvh] max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl sm:rounded-2xl ${isWeb3 ? 'bg-[#0a0a0f]' : 'bg-white'} shadow-2xl`}>
            <div className="flex items-center justify-between p-5 pb-3 flex-shrink-0">
              <h2 className={`text-lg font-bold ${tc}`}>Modifier l'organisation</h2>
              <button onClick={() => setShowEdit(false)} className={`p-2 rounded-xl ${labelBg} ${tm}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5">
              <div className="space-y-4">
                {editFields.map((f) => (
                  <div key={f.key}>
                    <label className={`text-xs font-medium ${tm} block mb-1`}>{f.label}</label>
                    {f.multiline ? (
                      <textarea
                        value={editForm[f.key]}
                        onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border focus:outline-none focus:border-primary transition text-sm resize-none`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={editForm[f.key]}
                        onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        className={`w-full px-4 py-3 rounded-xl ${inputBg} border focus:outline-none focus:border-primary transition text-sm`}
                      />
                    )}
                  </div>
                ))}
              </div>
              {editError && (
                <div className="mt-3 p-3 rounded-xl bg-red-500/10 text-red-500 text-sm">{editError}</div>
              )}
            </div>
            <div className={`flex gap-3 p-5 pt-3 flex-shrink-0 border-t border-ink/10`} style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={() => setShowEdit(false)}
                className={`flex-1 py-3 rounded-xl border ${isWeb3 ? 'border-white/10 text-white/60' : 'border-ink/10 text-ink/70'} font-medium`}
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 py-3 rounded-xl text-white font-semibold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-green)))' }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageOrganisation() {
  return <PageHub retour="account"><ContenuOrganisation /></PageHub>
}

